import $ from 'jquery';
import Swiper from 'swiper';
import { Navigation, Autoplay, Keyboard, Pagination, Thumbs } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import './sass/style.scss';
import './js/app';
import './js/responsive-content-tables.mjs';
import {
    getGalleryLightboxGroup,
    getVariationThumbSrc,
    shouldUseVariationImage,
} from './js/product-gallery-variation-order.mjs';
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';

window.Alpine = Alpine;
Alpine.plugin(collapse);
Alpine.start();

function initProductGallerySlider() {
    const mainGallery = document.querySelector('.js-product-gallery-main');

    if (!mainGallery) {
        return;
    }

    const thumbsGallery = document.querySelector('.js-product-gallery-thumbs');
    let thumbsSwiper = null;

    if (thumbsGallery) {
        thumbsSwiper = new Swiper(thumbsGallery, {
            slidesPerView: 'auto',
            spaceBetween: 8,
            watchSlidesProgress: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
        });
    }

    const productSwiperOptions = {
        modules: [Navigation, Pagination, Thumbs],
        allowTouchMove: true,
        simulateTouch: true,
        slidesPerView: 1,
        spaceBetween: 0,
        threshold: 5,
        touchRatio: 1,
        preventClicks: true,
        preventClicksPropagation: true,
        roundLengths: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        breakpoints: {
            1024: {
                allowTouchMove: false,
            },
        },
    };

    const paginationElement = mainGallery.querySelector('.swiper-pagination');
    if (paginationElement) {
        productSwiperOptions.pagination = {
            el: paginationElement,
            clickable: true,
        };
    }

    const prevGalleryButton = mainGallery.querySelector('.ProductGalleryNav--prev');
    const nextGalleryButton = mainGallery.querySelector('.ProductGalleryNav--next');
    if (prevGalleryButton && nextGalleryButton) {
        productSwiperOptions.navigation = {
            nextEl: nextGalleryButton,
            prevEl: prevGalleryButton,
        };
    }

    if (thumbsSwiper) {
        productSwiperOptions.thumbs = {
            swiper: thumbsSwiper,
        };
    }

    const mainSwiper = new Swiper(mainGallery, productSwiperOptions);
    const openVideoButton = mainGallery.querySelector('[data-gallery-open-video]');
    const galleryToggleButton = mainGallery.parentElement?.querySelector('[data-gallery-toggle]') || null;
    const galleryToggleLabel = galleryToggleButton?.querySelector('[data-gallery-toggle-label]') || null;
    const mainWrapper = mainGallery.querySelector('.swiper-wrapper');
    const thumbsWrapper = thumbsGallery?.querySelector('.swiper-wrapper') || null;
    const variationSlideSelector = '[data-product-gallery-variation-slide="true"]';
    const variationForm = document.querySelector('form.variations_form');
    const $variationForm = variationForm ? $(variationForm) : null;
    const variationStockStatus = variationForm?.querySelector('[data-product-stock-status]') || null;
    const desktopGalleryMedia = window.matchMedia('(min-width: 1024px)');
    const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    let desktopVideoRequested = false;
    let desktopVideoVisible = false;
    let desktopGalleryExpanded = false;
    const swiperStateClasses = [
        'swiper-slide-active',
        'swiper-slide-next',
        'swiper-slide-prev',
        'swiper-slide-visible',
        'swiper-slide-fully-visible',
        'swiper-slide-thumb-active',
    ];

    const updateVariationStockStatus = (variation = null) => {
        if (!variationStockStatus) {
            return;
        }

        const isInStock = variation ? Boolean(variation.is_in_stock) : true;
        const labelElement = variationStockStatus.querySelector('[data-product-stock-label]');
        const label = isInStock
            ? variationStockStatus.dataset.inStockLabel
            : variationStockStatus.dataset.outOfStockLabel;

        variationStockStatus.classList.toggle('is-in-stock', isInStock);
        variationStockStatus.classList.toggle('is-out-of-stock', !isInStock);

        if (labelElement && label) {
            labelElement.textContent = label;
        }
    };

    const getSlides = (wrapper) => {
        if (!wrapper) {
            return [];
        }

        return Array.from(wrapper.children).filter((slide) => slide.classList.contains('swiper-slide'));
    };

    const getFirstSlide = (wrapper) => getSlides(wrapper)[0] || null;
    const originalFirstMainSlide = getFirstSlide(mainWrapper)?.cloneNode(true) || null;
    const originalFirstThumbSlide = getFirstSlide(thumbsWrapper)?.cloneNode(true) || null;
    const restoreImageAttributes = [
        'src',
        'width',
        'height',
        'srcset',
        'sizes',
        'title',
        'data-caption',
        'alt',
        'data-src',
        'data-large_image',
        'data-large_image_width',
        'data-large_image_height',
    ];

    const restoreAttribute = (source, target, attribute) => {
        if (!source || !target) {
            return;
        }

        const value = source.getAttribute(attribute);

        if (value === null) {
            target.removeAttribute(attribute);
            return;
        }

        target.setAttribute(attribute, value);
    };

    const restoreFirstMainSlideImage = () => {
        if ($variationForm && typeof $variationForm.wc_variations_image_reset === 'function') {
            $variationForm.wc_variations_image_reset();
            return;
        }

        const firstMainSlide = getFirstSlide(mainWrapper);
        const originalImageWrap = originalFirstMainSlide?.querySelector(
            '.woocommerce-product-gallery__image, .woocommerce-product-gallery__image--placeholder',
        );
        const currentImageWrap = firstMainSlide?.querySelector(
            '.woocommerce-product-gallery__image, .woocommerce-product-gallery__image--placeholder',
        );
        const originalImage = originalFirstMainSlide?.querySelector('.wp-post-image');
        const currentImage = firstMainSlide?.querySelector('.wp-post-image');
        const originalLink = originalFirstMainSlide?.querySelector('.woocommerce-product-gallery__image a');
        const currentLink = firstMainSlide?.querySelector('.woocommerce-product-gallery__image a');

        restoreAttribute(originalImageWrap, currentImageWrap, 'data-thumb');
        restoreImageAttributes.forEach((attribute) => restoreAttribute(originalImage, currentImage, attribute));
        restoreAttribute(originalLink, currentLink, 'href');
    };

    const refreshGalleryLightbox = () => {
        const imageLinks = Array.from(mainGallery.querySelectorAll('.woocommerce-product-gallery__image a[href]'));
        const lightboxGroup = getGalleryLightboxGroup(imageLinks);

        imageLinks.forEach((link) => {
            link.classList.add('ari-fancybox');
            link.setAttribute('data-fancybox', link.getAttribute('data-fancybox') || lightboxGroup);
            link.setAttribute('data-fancybox-group', link.getAttribute('data-fancybox-group') || lightboxGroup);
        });

        const ariFancybox = window.ARI_FANCYBOX_INSTANCE;

        if (ariFancybox && typeof ariFancybox.convertElements === 'function') {
            ariFancybox.convertElements();
            return;
        }

        if ($.fn && typeof $.fn.fancybox === 'function') {
            $(imageLinks).fancybox(window.ARI_FANCYBOX?.lightbox || {});
        }
    };

    const prepareDynamicSlide = (slide) => {
        if (!slide) {
            return null;
        }

        const clone = slide.cloneNode(true);
        clone.setAttribute('data-product-gallery-variation-slide', 'true');
        clone.removeAttribute('style');
        swiperStateClasses.forEach((className) => clone.classList.remove(className));

        return clone;
    };

    const createVariationThumbSlide = (variation) => {
        if (!originalFirstThumbSlide) {
            return null;
        }

        const thumbSrc = getVariationThumbSrc(variation);

        if (!thumbSrc) {
            return null;
        }

        const thumbSlide = prepareDynamicSlide(originalFirstThumbSlide);
        const thumbImage = thumbSlide?.querySelector('img');

        if (!thumbImage) {
            return null;
        }

        const variationImage = variation?.image || {};
        thumbImage.setAttribute('src', thumbSrc);
        thumbImage.removeAttribute('srcset');
        thumbImage.removeAttribute('sizes');
        thumbImage.setAttribute('alt', variationImage.alt || variationImage.title || '');

        return thumbSlide;
    };

    const removeVariationSlides = () => {
        [mainWrapper, thumbsWrapper].forEach((wrapper) => {
            getSlides(wrapper).forEach((slide) => {
                if (slide.matches(variationSlideSelector)) {
                    slide.remove();
                }
            });
        });
    };

    const restoreFirstSlides = () => {
        restoreFirstMainSlideImage();

        if (thumbsWrapper && originalFirstThumbSlide) {
            const firstThumbSlide = getFirstSlide(thumbsWrapper);

            if (firstThumbSlide) {
                firstThumbSlide.replaceWith(originalFirstThumbSlide.cloneNode(true));
            }
        }
    };

    const prependVariationSlides = (mainSlide, thumbSlide) => {
        if (mainWrapper && mainSlide) {
            mainWrapper.insertBefore(mainSlide, mainWrapper.firstElementChild);
        }

        if (thumbsWrapper && thumbSlide) {
            thumbsWrapper.insertBefore(thumbSlide, thumbsWrapper.firstElementChild);
        }
    };

    const getVideoSlide = () =>
        Array.from(mainGallery.querySelectorAll('.swiper-slide')).find((slide) => slide.querySelector('video')) ||
        null;

    const setDesktopGalleryExpanded = (expanded) => {
        desktopGalleryExpanded = Boolean(expanded);
        mainGallery.classList.toggle('is-expanded', desktopGalleryExpanded);

        if (!galleryToggleButton) {
            return;
        }

        galleryToggleButton.setAttribute('aria-expanded', desktopGalleryExpanded ? 'true' : 'false');

        if (galleryToggleLabel) {
            galleryToggleLabel.textContent = desktopGalleryExpanded
                ? galleryToggleLabel.dataset.expandedLabel
                : galleryToggleLabel.dataset.collapsedLabel;
        }
    };

    const playGalleryVideo = (videoElement) => {
        if (!videoElement) {
            return;
        }

        videoElement.muted = true;
        const playPromise = videoElement.play();

        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
        }
    };

    const syncGalleryVideosPlayback = () => {
        const videos = mainGallery.querySelectorAll('video');
        const isDesktopGrid = desktopGalleryMedia.matches;
        const videoSlide = getVideoSlide();

        videos.forEach((videoElement) => {
            const slide = videoElement.closest('.swiper-slide');
            const shouldPlay = isDesktopGrid
                ? desktopVideoRequested && desktopVideoVisible && slide === videoSlide
                : slide?.classList.contains('swiper-slide-active');

            if (shouldPlay) {
                playGalleryVideo(videoElement);
            } else {
                videoElement.pause();
            }
        });

        if (openVideoButton) {
            const videoSlideIndex = videoSlide
                ? Array.from(mainGallery.querySelectorAll('.swiper-slide')).indexOf(videoSlide)
                : -1;
            const shouldHideButton =
                videoSlideIndex < 0 || (!isDesktopGrid && videoSlideIndex === mainSwiper.activeIndex);
            openVideoButton.classList.toggle('is-hidden', shouldHideButton);
        }
    };

    const updateGallerySwipers = () => {
        if (thumbsSwiper) {
            thumbsSwiper.update();
        }

        mainSwiper.update();

        if (mainSwiper.pagination && typeof mainSwiper.pagination.render === 'function') {
            mainSwiper.pagination.render();
            mainSwiper.pagination.update();
        }

        if (mainSwiper.thumbs && typeof mainSwiper.thumbs.update === 'function') {
            mainSwiper.thumbs.update();
        }
    };

    const showFirstGallerySlide = () => {
        refreshGalleryLightbox();
        updateGallerySwipers();
        mainSwiper.slideTo(0, 0);
        thumbsSwiper?.slideTo(0, 0);
        syncGalleryVideosPlayback();
    };

    const resetVariationGallerySlide = () => {
        removeVariationSlides();
        restoreFirstSlides();
        showFirstGallerySlide();
    };

    const showVariationGallerySlide = (variation) => {
        if (!shouldUseVariationImage(variation)) {
            resetVariationGallerySlide();
            return;
        }

        const variationMainSlide = prepareDynamicSlide(getFirstSlide(mainWrapper));
        const variationThumbSlide = createVariationThumbSlide(variation);

        removeVariationSlides();
        restoreFirstSlides();
        prependVariationSlides(variationMainSlide, variationThumbSlide);
        showFirstGallerySlide();
    };

    if ($variationForm) {
        $variationForm.off('.productGalleryVideoFix');
        $variationForm.on('found_variation.productGalleryVideoFix', (event, variation) => {
            updateVariationStockStatus(variation);
            setTimeout(() => showVariationGallerySlide(variation), 30);
        });
        $variationForm.on('reset_image.productGalleryVideoFix hide_variation.productGalleryVideoFix', () => {
            updateVariationStockStatus();
            setTimeout(resetVariationGallerySlide, 30);
        });
    }

    if (openVideoButton) {
        openVideoButton.addEventListener('click', (event) => {
            event.preventDefault();

            const videoSlide = getVideoSlide();

            if (!videoSlide) {
                return;
            }

            if (desktopGalleryMedia.matches) {
                desktopVideoRequested = true;
                setDesktopGalleryExpanded(true);
                videoSlide.classList.add('is-video-requested');
                playGalleryVideo(videoSlide.querySelector('video'));
                videoSlide.scrollIntoView({
                    behavior: reducedMotionMedia.matches ? 'auto' : 'smooth',
                    block: 'center',
                    inline: 'nearest',
                });
                syncGalleryVideosPlayback();
                return;
            }

            const videoSlideIndex = Array.from(mainGallery.querySelectorAll('.swiper-slide')).indexOf(videoSlide);
            mainSwiper.slideTo(videoSlideIndex, reducedMotionMedia.matches ? 0 : 300);
            syncGalleryVideosPlayback();
        });
    }

    galleryToggleButton?.addEventListener('click', () => {
        setDesktopGalleryExpanded(!desktopGalleryExpanded);
    });

    const videoSlide = getVideoSlide();
    if (videoSlide && 'IntersectionObserver' in window) {
        const videoVisibilityObserver = new IntersectionObserver(
            ([entry]) => {
                desktopVideoVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15);
                syncGalleryVideosPlayback();
            },
            {
                threshold: [0, 0.15, 0.5],
            },
        );

        videoVisibilityObserver.observe(videoSlide);
    } else {
        desktopVideoVisible = true;
    }

    const handleGalleryBreakpointChange = () => {
        syncGalleryVideosPlayback();
    };

    if (typeof desktopGalleryMedia.addEventListener === 'function') {
        desktopGalleryMedia.addEventListener('change', handleGalleryBreakpointChange);
    } else if (typeof desktopGalleryMedia.addListener === 'function') {
        desktopGalleryMedia.addListener(handleGalleryBreakpointChange);
    }

    syncGalleryVideosPlayback();
    mainSwiper.on('slideChange', syncGalleryVideosPlayback);
    mainSwiper.on('transitionStart', syncGalleryVideosPlayback);
}

initProductGallerySlider();

function updateCategoryTilesNavVisibility(swiper) {
    const sliderElement = swiper?.el;
    const navRoot = sliderElement?.parentElement;
    const prevBtn = navRoot?.querySelector('.categories-tiles-swiper-nav-left');
    const nextBtn = navRoot?.querySelector('.categories-tiles-swiper-nav-right');
    const isLocked = Boolean(swiper?.isLocked);

    navRoot?.classList.toggle('is-locked', isLocked);

    [prevBtn, nextBtn].forEach((button) => {
        if (!button) {
            return;
        }

        button.hidden = isLocked;
        button.setAttribute('aria-hidden', isLocked ? 'true' : 'false');
    });
}

document.querySelectorAll('.categories-tiles-swiper').forEach((sliderElement) => {
    const prevEl = sliderElement.parentElement.querySelector('.categories-tiles-swiper-nav-left');
    const nextEl = sliderElement.parentElement.querySelector('.categories-tiles-swiper-nav-right');

    const categoryTilesSwiper = new Swiper(sliderElement, {
        modules: [Navigation],
        slidesPerView: 'auto',
        spaceBetween: 16,
        watchOverflow: true,
        breakpoints: {
            1024: {
                spaceBetween: 35,
            },
        },
        navigation: {
            nextEl,
            prevEl,
        },
    });

    categoryTilesSwiper.on('init slideChange update resize lock unlock', updateCategoryTilesNavVisibility);
    updateCategoryTilesNavVisibility(categoryTilesSwiper);
});

function initArchiveProductViewToggle() {
    document.querySelectorAll('.ArchiveProduct').forEach((archiveElement) => {
        const productWrap = archiveElement.querySelector('.ArchiveProduct__productWrap');
        const viewButtons = archiveElement.querySelectorAll('.ArchiveProduct__viewButton');

        if (!productWrap || !viewButtons.length) {
            return;
        }

        const setViewMode = (viewMode) => {
            const normalizedViewMode = viewMode === 'list' ? 'list' : 'grid';

            productWrap.classList.toggle(
                'ArchiveProduct__productWrap--list',
                normalizedViewMode === 'list',
            );

            viewButtons.forEach((button) => {
                const isActive = button.dataset.viewMode === normalizedViewMode;

                button.classList.toggle('is-active', isActive);
                button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
        };

        viewButtons.forEach((button) => {
            button.addEventListener('click', () => {
                setViewMode(button.dataset.viewMode);
            });
        });

        const activeButton = archiveElement.querySelector('.ArchiveProduct__viewButton.is-active');
        setViewMode(activeButton?.dataset.viewMode || 'grid');
    });
}

initArchiveProductViewToggle();

function initSwiper() {
    document.querySelectorAll('[data-home-hero-slider]').forEach((slider) => {
        const slides = slider.querySelectorAll('.HomeHero__slide');

        if (slides.length < 2) {
            return;
        }

        new Swiper(slider, {
            loop: true,
            speed: 700,
            modules: [Navigation, Pagination, Autoplay, Keyboard],
            navigation: {
                nextEl: slider.querySelector('[data-home-hero-next]'),
                prevEl: slider.querySelector('[data-home-hero-prev]'),
            },
            pagination: {
                el: slider.querySelector('[data-home-hero-pagination]'),
                clickable: true,
            },
            autoplay: {
                delay: 6000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
            keyboard: {
                enabled: true,
            },
            watchOverflow: true,
        });
    });
}

$(window).on('load', initSwiper);

$(document).on('change', '#select_all', function () {
    if ($(this).is(':checked')) {
        $(this).parent().parent().find('input[type=checkbox]').prop('checked', true);
    } else {
        $(this).parent().parent().find('input[type=checkbox]').prop('checked', false);
    }
});

$('#show_coupon_form').on('click', () => {
    $('#coupon_form').slideToggle();
});

$(document).on('change', '.variation-switcher', function () {
    const cart_item_key = $(this).data('cart-item-key');
    const variation_id = $(this).find('input[type="radio"]:checked').val();

    $.ajax({
        type: 'POST',
        url: jsData.ajaxUrl,
        data: {
            action: 'change_variation_in_cart',
            cart_item_key,
            variation_id,
        },
        success(response) {
            if (response.success) {
                $(document.body).trigger('update_checkout');
            } else {
                alert('Error: Unable to change variation.');
            }
        },
    });
});

$('a.Button.slideable').on('click', function (e) {
    e.preventDefault();

    const target = $(this).attr('href');
    const hashIndex = target.indexOf('#');

    if (hashIndex !== -1) {
        const targetId = target.substring(hashIndex);

        if ($(targetId).length) {
            const headerHeight = $('.Header').outerHeight();
            const targetPosition = $(targetId).offset().top - headerHeight;

            $('html, body').animate(
                {
                    scrollTop: targetPosition,
                },
                400,
            );
        }
    }
});

const initializeCounters = (root = document) => {
    const counters = root.querySelectorAll('[data-component="counter"]');
    const addZero = (n) => (n < 10 ? `0${n}` : n);
    const getDuration = (type) => {
        const v = parseInt(type, 10);
        return type.endsWith('m') ? v * 60 * 1000 : v * 60 * 60 * 1000;
    };
    const formatBlocks = ({ h, m, s }, showH) =>
        [
            showH &&
                `
        <div class="flex items-center w-[56px] h-[40px] rounded-[2px] bg-white/10 flex-col justify-center !text-white text-[12px] md:text-base font-normal">
            ${h}
            <div class="!text-[12px] ml-[2px] md:ml-0 !font-medium !text-white/60 block md:h-[10px] md:leading-[0px] md:mt-1">Godzin</div>
        </div>`,
            `
        <div class="flex items-center w-[56px] h-[40px] rounded-[2px] bg-white/10 flex-col justify-center !text-white text-[12px] md:text-base font-normal">
            ${addZero(m)}
            <div class="!text-[12px] ml-[2px] md:ml-0 !font-medium !text-white/60 block md:h-[10px] md:leading-[0px] md:mt-1">Minut</div>
        </div>`,
            `
        <div class="flex items-center w-[56px] h-[40px] rounded-[2px] bg-white/10 flex-col justify-center !text-white text-[12px] md:text-base font-normal">
            ${addZero(s)}
            <div class="!text-[12px] ml-[2px] md:ml-0 !font-medium !text-white/60 block md:h-[10px] md:leading-[0px] md:mt-1">Sekund</div>
        </div>`,
        ]
            .filter(Boolean)
            .join('');

    counters.forEach((counter) => {
        const type = counter.dataset.type || '24h';
        let endTime;
        if (type === '24h') {
            const now = new Date();
            const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
            endTime = midnight.getTime();
        } else {
            endTime = Date.now() + getDuration(type);
        }
        const showHours = !type.endsWith('m');

        const tick = () => {
            const diff = endTime - Date.now();
            if (diff <= 0) {
                clearInterval(timerId);
                counter.innerHTML = '';
                return;
            }
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            counter.innerHTML = formatBlocks({ h, m, s }, showHours);
        };

        const timerId = setInterval(tick, 1000);
        tick();
    });
};

window.initializeCounters = initializeCounters;

document.addEventListener('DOMContentLoaded', () => {
    initializeCounters();

    const faqItemBtns = document.querySelectorAll('.faq-item-btn');
    const faqSections = document.querySelectorAll('.faq-section');

    function addActiveClass(button) {
        faqItemBtns.forEach((btn) => btn.classList.remove('active-faq-item-btn'));
        faqSections.forEach((section) => {
            section.classList.add('hidden');
            section.classList.remove('hidden');
        });

        const sectionId = button.getAttribute('href').replace('#', '').trim();

        faqSections.forEach((section) => {
            section.classList.add('hidden');
            if (section.getAttribute('data-id') === sectionId) {
                section.classList.remove('hidden');
            }
        });

        button.classList.add('active-faq-item-btn');

        $('html, body').animate({
            scrollTop: $(`[data-id="${sectionId}"]`).offset().top - $('.Header').height() - 20,
        });
    }

    faqItemBtns.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            addActiveClass(button);
        });
    });

    window.addEventListener('scroll', function () {
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollPercentage = (scrollPosition / documentHeight) * 100;

        const button = document.getElementById('go-top-button');
        if (!button) {
            return;
        }
        if (scrollPercentage >= 95) {
            button.style.display = 'flex';
        } else {
            button.style.display = 'none';
        }
    });

    $('#go-top-button').on('click', () => {
        $('html, body').animate({ scrollTop: 0 });
        return false;
    });
});

// let current = Math.floor(Math.random() * 61) + 10;

// function updatePeopleCounter() {
//     const change = Math.floor(Math.random() * 3) + 1;
//     const direction = Math.random() < 0.5 ? -1 : 1;
//     let next = current + change * direction;
//     if (next < 10) next = 10;
//     if (next > 70) next = 70;
//     current = next;
//     $('#single-product-people-view-counter').html(current);
//     const timeout = Math.floor(Math.random() * 3000) + 8000;
//     setTimeout(updatePeopleCounter, timeout);
// }

// $('#single-product-people-view-counter').html(current);
// updatePeopleCounter();

$(window).load(function () {
    const faqLinks = $('.flex.flex-col a[href^="#"]');
    const faqSections = $('.faq-section');
    const scrollOffset = 40;

    faqLinks.on('click', function (e) {
        e.preventDefault();
        const targetId = $(this).attr('href');
        const $target = $(targetId);
        if ($target.length) {
            $('html, body').animate(
                {
                    scrollTop: $target.offset().top - scrollOffset,
                },
                400,
            );
        }
    });

    // Podświetlanie aktywnego linku podczas scrola
    $(window).on('scroll', function () {
        const scrollPosition = $(window).scrollTop() + scrollOffset + 60;

        faqSections.each(function () {
            const sectionTop = $(this).offset().top;
            const sectionBottom = sectionTop + $(this).outerHeight();
            const sectionId = $(this).attr('id');
            if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                faqLinks.removeClass('active-faq-item-btn');
                faqLinks.filter(`[href="#${sectionId}"]`).addClass('active-faq-item-btn');
            }
        });
    });
});

if (document.querySelector('.latest-posts-swiper')) {
    new Swiper('.latest-posts-swiper', {
        modules: [Pagination],
        slidesPerView: 1,
        spaceBetween: 16,
        breakpoints: {
            768: {
                slidesPerView: 3,
            },
        },
        pagination: { el: '.blog-pagination', clickable: true },
    });
}

if (window.innerWidth < 768) {
    document.querySelectorAll('[data-collapsible]').forEach((section) => {
        const toggle = section.querySelector('.footer-toggle');
        const content = section.querySelector('.footer-section-content');
        if (!toggle) return;

        toggle.addEventListener('click', () => {
            const expanded = toggle.getAttribute('aria-expanded') === 'true';
            toggle.setAttribute('aria-expanded', !expanded);
            content.classList.toggle('hidden');
            toggle.querySelector('.icon-plus').classList.toggle('hidden');
            toggle.querySelector('.icon-minus').classList.toggle('hidden');
        });
    });
}

function findSwatchButton(target) {
    if (!(target instanceof Element)) {
        return null;
    }

    return target.closest('.wc-swatch');
}

function findSwatchContainer(target) {
    if (!(target instanceof Element)) {
        return null;
    }

    return target.closest('.wc-swatches');
}

function updateLoopProductImageFromSwatch(buttonEl) {
    if (!(buttonEl instanceof Element)) {
        return;
    }

    const productEl = buttonEl.closest('.product');
    if (!(productEl instanceof Element)) {
        return;
    }

    const imageEl = productEl.querySelector('img');
    const src = buttonEl.getAttribute('data-image') || '';
    const srcset = buttonEl.getAttribute('data-srcset') || '';
    const sizes = buttonEl.getAttribute('data-sizes') || '';

    if (!(imageEl instanceof HTMLImageElement) || !src) {
        return;
    }

    imageEl.setAttribute('src', src);
    if (srcset) {
        imageEl.setAttribute('srcset', srcset);
    } else {
        imageEl.removeAttribute('srcset');
    }

    if (sizes) {
        imageEl.setAttribute('sizes', sizes);
    } else {
        imageEl.removeAttribute('sizes');
    }

    const siblings = buttonEl.parentElement
        ? buttonEl.parentElement.querySelectorAll('.wc-swatch')
        : [];
    siblings.forEach((node) => node.classList.remove('is-active'));
    buttonEl.classList.add('is-active');
}

function swallowSwatchEvent(event) {
    const swatchButton = findSwatchButton(event.target);
    const swatchContainer = swatchButton
        ? findSwatchContainer(swatchButton)
        : findSwatchContainer(event.target);

    if (!swatchContainer) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
    }

    if (swatchButton && event.type === 'click') {
        updateLoopProductImageFromSwatch(swatchButton);
    }
}

[window, document].forEach((target) => {
    target.addEventListener('pointerdown', swallowSwatchEvent, true);
    target.addEventListener('mousedown', swallowSwatchEvent, true);
    target.addEventListener(
        'touchstart',
        function (event) {
            swallowSwatchEvent(event);
        },
        { capture: true, passive: false },
    );
    target.addEventListener('click', swallowSwatchEvent, true);
});

// (function () {
//     // Guard - tylko jedna inicjalizacja
//     if (window.__verenzaInfiniteInit) return;
//     window.__verenzaInfiniteInit = true;

//     if (!('IntersectionObserver' in window)) return;

//     const body = document.body;
//     body.classList.add('js-infinite-enabled');

//     // Siatka i sentinel
//     const grid = document.querySelector('.products');
//     const sentinel = document.getElementById('infinite-sentinel');
//     if (!grid || !sentinel) return;

//     // Helpery
//     const STORAGE_KEY = `verenza:archiveState:${location.pathname}${location.search}`;

//     // Zwraca listę kart-produktów dla danego root
//     function getItems(root) {
//         const scope = root || document;
//         const productsEl = scope.querySelector('.products') || scope;
//         // dzieci .products, które mają klasę .product (u Ciebie to <div class="LoopProduct ... product ...">)
//         return productsEl.querySelectorAll(':scope > .product');
//     }

//     // ID produktu z karty
//     function getProductId(el) {
//         if (!el) return null;
//         // preferuj data-product-id na karcie (jeśli dodasz)
//         const d = el.getAttribute('data-product-id');
//         if (d) return d;
//         // klasa post-123 na karcie
//         const m = el.className && el.className.match(/\bpost-(\d+)\b/);
//         if (m) return m[1];
//         // awaryjnie: spróbuj znaleźć w swatchach
//         const sw = el.querySelector('[data-product-id]');
//         if (sw) return sw.getAttribute('data-product-id');
//         return null;
//     }

//     // href produktu (do dodatkowego dedupe)
//     function getProductHref(el) {
//         const a = el.querySelector('a.woocommerce-LoopProduct-link, a[href*="/produkt/"]');
//         return a ? a.getAttribute('href') : null;
//     }

//     // Zestawy antyduplikacyjne
//     let seenIds = new Set();
//     let seenHrefs = new Set();

//     // Seed z aktualnej siatki i od razu oczyść ewentualne duplikaty obecne w DOM
//     Array.from(getItems(document)).forEach((item) => {
//         const id = getProductId(item);
//         const href = getProductHref(item);
//         const hasId = id && seenIds.has(id);
//         const hasHref = href && seenHrefs.has(href);
//         if (hasId || hasHref) {
//             item.remove();
//         } else {
//             if (id) seenIds.add(id);
//             if (href) seenHrefs.add(href);
//         }
//     });

//     // Paginacja -> „następny” URL: pierwszy link po .is-current w .Pagination-pages
//     function getNextUrl(doc) {
//         const root = doc || document;
//         const pages = root.querySelector('.Pagination .Pagination-pages');
//         if (!pages) return null;
//         const current = pages.querySelector('.is-current');
//         if (!current) return null;
//         let node = current.nextElementSibling;
//         while (node && node.nodeType === 1 && node.tagName !== 'A') node = node.nextElementSibling;
//         return node && node.tagName === 'A' ? node.getAttribute('href') : null;
//     }

//     // Restore z sessionStorage
//     let nextUrl = null;
//     let loading = false;
//     let done = false;

//     try {
//         const raw = sessionStorage.getItem(STORAGE_KEY);
//         if (raw) {
//             const saved = JSON.parse(raw);
//             if (saved && saved.html && Date.now() - (saved.ts || 0) < 30 * 60 * 1000) {
//                 grid.innerHTML = saved.html;
//                 nextUrl = saved.nextUrl || null;

//                 // Przywróć zbiory widzianych, jeśli były zapisane
//                 seenIds = new Set(saved.seenIds || []);
//                 seenHrefs = new Set(saved.seenHrefs || []);

//                 // Dodatkowo przeleć DOM i usuń ewentualne duplikaty
//                 Array.from(getItems(document)).forEach((item) => {
//                     const id = getProductId(item);
//                     const href = getProductHref(item);
//                     const dup = (id && seenIds.has(id)) || (href && seenHrefs.has(href));
//                     if (dup) {
//                         // pierwszy egzemplarz powinien trafić do setu - więc jeśli nie ma,
//                         // dodaj i zostaw, a duplikaty usuń
//                         if (id && !seenIds.has(id)) seenIds.add(id);
//                         if (href && !seenHrefs.has(href)) seenHrefs.add(href);
//                     }
//                 });

//                 if (!nextUrl) {
//                     done = true;
//                     sentinel.style.display = 'none';
//                 }
//                 requestAnimationFrame(() => window.scrollTo(0, saved.scrollY || 0));
//             }
//         }
//     } catch (_) {}

//     if (!nextUrl) {
//         nextUrl = getNextUrl(document);
//         done = !nextUrl;
//         if (done) sentinel.style.display = 'none';
//     }

//     // Zapis stanu przed wyjściem lub kliknięciem w kartę
//     function saveState() {
//         try {
//             const payload = {
//                 html: grid.innerHTML,
//                 nextUrl,
//                 scrollY: window.scrollY,
//                 seenIds: Array.from(seenIds),
//                 seenHrefs: Array.from(seenHrefs),
//                 ts: Date.now(),
//             };
//             sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
//         } catch (_) {}
//     }

//     // Klik w produkty -> zapisz stan
//     grid.addEventListener(
//         'click',
//         (e) => {
//             const a = e.target.closest('a');
//             if (!a) return;
//             saveState();
//         },
//         { capture: true },
//     );

//     window.addEventListener('beforeunload', saveState);

//     // Infinite scroll z antyduplikacją
//     const io = new IntersectionObserver(
//         async (entries) => {
//             const entry = entries[0];
//             if (!entry || !entry.isIntersecting || loading || done) return;
//             if (!nextUrl) {
//                 done = true;
//                 sentinel.style.display = 'none';
//                 io.disconnect();
//                 return;
//             }

//             loading = true;
//             try {
//                 const res = await fetch(nextUrl, {
//                     headers: { 'X-Requested-With': 'XMLHttpRequest' },
//                 });
//                 if (!res.ok) throw new Error(`HTTP ${res.status}`);
//                 const html = await res.text();

//                 const parser = new DOMParser();
//                 const doc = parser.parseFromString(html, 'text/html');

//                 const candidates = Array.from(getItems(doc));
//                 const frag = document.createDocumentFragment();

//                 for (const item of candidates) {
//                     const id = getProductId(item);
//                     const href = getProductHref(item);
//                     if ((id && seenIds.has(id)) || (href && seenHrefs.has(href))) continue;
//                     if (id) seenIds.add(id);
//                     if (href) seenHrefs.add(href);
//                     frag.appendChild(item);
//                 }

//                 if (frag.childNodes.length) grid.appendChild(frag);

//                 // Następny URL z nowej paginacji
//                 const fresh = getNextUrl(doc);
//                 nextUrl = fresh || null;
//                 if (!nextUrl) {
//                     done = true;
//                     sentinel.style.display = 'none';
//                     io.disconnect();
//                 }

//                 // Zapis po dociągnięciu
//                 saveState();

//                 // Jeśli masz jakieś re-inity (np. swatches), wywołaj je tutaj
//                 // window.initSwatches?.(grid);
//             } catch (e) {
//                 console.error('Infinite scroll error:', e);
//                 done = true;
//                 sentinel.style.display = 'none';
//                 body.classList.remove('js-infinite-enabled');
//             } finally {
//                 loading = false;
//             }
//         },
//         { rootMargin: '600px 0px 800px 0px', threshold: 0 },
//     );

//     io.observe(sentinel);

//     // Respect reduced motion
//     if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
//         io.disconnect();
//         body.classList.remove('js-infinite-enabled');
//         sentinel.style.display = 'none';
//     }
// })();
